import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import type { Round } from '../data/types';
import PlayView from './PlayView';

/* Team preview: /rounds/:roundId/preview[/:slug]

   The same payload and the same components a share link serves, but behind the roster gate —
   so anyone on the team can check a round, or one page in it, without minting a public link
   first. That was the previous route to seeing your own work: create a link, copy it, open it.

   Always fetched fresh (no cache), because the entire point is reviewing the latest saved
   edits. */

export default function PreviewRound() {
  const { roundId = '', slug } = useParams();
  const [round, setRound] = useState<Round | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const loaded = await api.preview(roundId);
        if (!cancelled) setRound(loaded);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load that series.');
      }
    })();
    return () => { cancelled = true; };
  }, [roundId]);

  if (error) {
    return (
      <div className="centred">
        <div className="round-title">Preview</div>
        <div className="big">Could not open this series</div>
        <div className="sub">{error}</div>
        <Link className="btn primary" to={`/rounds/${roundId}`} style={{ textDecoration: 'none' }}>
          Back to the Editor
        </Link>
      </div>
    );
  }

  if (!round) return <div className="centred"><div className="big">Loading preview…</div></div>;

  if (round.items.length === 0) {
    return (
      <div className="centred">
        <div className="round-title">Real or Fake?</div>
        <div className="big">Nothing to preview yet</div>
        <div className="sub">This series has no headlines in it.</div>
        <Link className="btn primary" to={`/rounds/${roundId}`} style={{ textDecoration: 'none' }}>
          Back to the Editor
        </Link>
      </div>
    );
  }

  if (slug && !round.items.some((i) => i.slug === slug || i.id === slug)) {
    return (
      <div className="centred">
        <div className="round-title">Real or Fake?</div>
        <div className="big">That page is not in this series</div>
        <div className="sub">It may have been deleted since the link was shared.</div>
        <Link className="btn primary" to={`/rounds/${roundId}/preview`} style={{ textDecoration: 'none' }}>
          Play the Whole Series
        </Link>
      </div>
    );
  }

  /* Recorded as `preview`, which is the flag that stops it marking the series as recorded.
     Checking your own work is not a shoot. */
  return <PlayView round={round} focusSlug={slug} session={{ source: 'preview', key: roundId }} />;
}
